import { Card, Skeleton, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { useContext } from "react";
import "react-perfect-scrollbar/dist/css/styles.css";
import { useNavigate } from "react-router-dom";
import LoaderContext from "../../Context/LoaderContext/Index";
import {
  InterviewSession,
  MatchResult,
  Question,
  Userprofile,
} from "../../Interface/Index";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import DetailedRequirementList from "./DetailedRequirementList";
import "./index.scss";
import { DashboardContext } from "../../Context/DashboardContext";
import ModalContext from "../../Context/ModalContext/Index";
import AgentService from "../../Services/agent/agent.service";
import messages from "../../Services/agent/messages";
import InterviewService from "../../Services/interview.service";
import {
  JobApplication,
  JobApplicationStatus,
} from "../../Entities/job-application.entity";
import JobApplicationService from "../../Services/job.application.service";
import ResumeUploader from "../../Components/ResumeUploader";

const { Title, Paragraph } = Typography;

const Requirements = () => {
  const navigate = useNavigate();

  const profileMatcherService = new ProfileMatcherService();
  const agentService = AgentService.getInstance();
  const interviewService = new InterviewService();
  const jobApplicationService = new JobApplicationService();

  const { showUploadCV, handleToggleModal } = useContext(ModalContext);
  const {
    appliedJob,
    jobDescription,
    addSession,
    enableRounds,
    enabledInterviewRoundTabs,
    setAppliedJob,
    setShowResultTab,
    setShowOnlineInterviewTab,
  } = useContext(DashboardContext);
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const handleUnqualifiedProfileMatchResult = async (
    matchResult: MatchResult
  ) => {
    const statausToPoll = "rejected";
    const updatedApplication = await checkApplicationStatus(
      matchResult.jobApplicationId,
      statausToPoll
    );
    if (updatedApplication) {
      setAppliedJob(updatedApplication);
    }
    setTimeout(() => {
      setLoading(false);
      setLoadingMessage(null);
    }, 500);
    setShowResultTab(true);
    agentService.setCurrentMessage(messages.MATCH_RESULT_UNQUALIFIED, "", "");
    navigate(`/${jobDescription?._id}/result`);
    return;
  };

  const handleQualifiedProfileMatchResult = async (
    matchResult: MatchResult
  ) => {
    const { jobApplicationId } = matchResult;

    const isInterviewRoundsExists = enabledInterviewRoundTabs.length > 0;
    if (!isInterviewRoundsExists) {
      await handleNoInterviewExists(jobApplicationId);
      return;
    } else {
      await handleNextInterviewRound(jobApplicationId);
      return;
    }
  };

  const checkApplicationStatus = async (
    id: string,
    status: JobApplicationStatus
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.status === status) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const handleNoInterviewExists = async (jobApplicationId: string) => {
    setLoadingMessage("Setting up online interview...");
    agentService.setCurrentMessage(messages.EVALUATION_QUALIFIED, "", "");
    try {
      const statusToCheck: JobApplicationStatus =
        jobDescription?.onlineInterview ? "interview" : "shortlisted";

      const updatedJobApplication = await checkApplicationStatus(
        jobApplicationId,
        statusToCheck
      );

      if (updatedJobApplication) {
        setAppliedJob(updatedJobApplication);

        if (statusToCheck === "shortlisted") {
          agentService.setCurrentMessage(
            messages.WAITING_FOR_INTERVIEW_SCHEDULE,
            "",
            "Select Slot"
          );
          setShowResultTab(true);
          navigate(`/${jobDescription?._id}/result`);
        } else if (statusToCheck === "interview") {
          setShowOnlineInterviewTab(true);
          agentService.setCurrentMessage(
            messages.WAITING_FOR_INTERVIEW_SCHEDULE,
            "",
            "Select Slot"
          );
          navigate(`/${jobDescription?._id}/online-interview`);
        }
      }
    } catch (error) {
      console.log("handleNoInterviewExists error", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleNextInterviewRound = async (jobApplicationId: string) => {
    agentService.setCurrentMessage(
      messages.WAITING_FOR_INTERVIEW_SESSION_CREATION,
      "",
      ""
    );
    setLoadingMessage("Creating interview session...");

    const pollForSession = async (): Promise<InterviewSession[]> => {
      try {
        const sessions = await interviewService.getAllInterviewSessionById(
          jobApplicationId
        );

        if (sessions && sessions.length > 0) {
          return sessions;
        } else {
          console.log("No session found. Retrying...");
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return await pollForSession();
        }
      } catch (error) {
        console.error("Error fetching session:", error);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return await pollForSession();
      }
    };

    const sessions = await pollForSession();

    if (sessions && sessions.length > 0) {
      agentService.setCurrentMessage(messages.GENERATING_QUESTIONNAIRE, "", "");
      setLoadingMessage("Generating questionnaire...");
      const firstSession = sessions[0];
      const pollForQuestions = async (): Promise<Question[]> => {
        try {
          const updatedSession = await interviewService.findSessionById(
            firstSession._id
          );

          if (updatedSession && updatedSession.isQuestionsGenerated) {
            const sessionQuestions =
              await interviewService.getQuestionsBySessionId(firstSession._id);
            return sessionQuestions;
          } else {
            console.log("No questions found. Retrying...");
            await new Promise((resolve) => setTimeout(resolve, 3000));
            return await pollForQuestions();
          }
        } catch (error) {
          console.error("Error fetching questions:", error);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          return await pollForQuestions();
        }
      };
      const sessionQuestions = await pollForQuestions();
      if (sessionQuestions && sessionQuestions.length > 0) {
        const roundName = enabledInterviewRoundTabs.find(
          (round) => round.roundId === firstSession.interviewRoundId
        )?.roundName;
        enableRounds(firstSession.interviewRoundId, firstSession._id);
        addSession(firstSession);
        setLoading(false);
        setLoadingMessage(null);
        agentService.setCurrentMessage(
          messages.WAITING_FOR_INTERVIEW_ROUND_COMPLETION,
          { roundName: roundName },
          "View Questions"
        );
        navigate(`/${jobDescription?._id}/interview-round/${firstSession._id}`);
      }
    }
  };

  const handleFileUploadAndAnalyze = async (file: File) => {
    if (!appliedJob) {
      console.error("handleFileUploadAndAnalyze -> no applied job");
      return;
    }

    setLoading(true);
    setLoadingMessage("Matching your resume...");
    agentService.setCurrentMessage(messages.WAITING_FOR_MATCH_RESULTS, "", "");
    const matchResult =
      await profileMatcherService.uploadResumeToS3AndMatchProfile(
        file,
        appliedJob._id
      );

    // TODO : handle profile match API failure
    if (!matchResult) return;

    if (matchResult.status === "unqualified") {
      return await handleUnqualifiedProfileMatchResult(matchResult);
    } else if (matchResult.status === "qualified") {
      return await handleQualifiedProfileMatchResult(matchResult);
    }
  };

  const handleContinueWithProfile = async (profile: Userprofile) => {
    if (!appliedJob) {
      console.error("handleContinueWithProfile -> no applied job");
      return;
    }
    setLoading(true);
    setLoadingMessage("Matching your resume...");
    AgentService.getInstance().setCurrentMessage(
      messages.WAITING_FOR_MATCH_RESULTS,
      "",
      ""
    );

    const matchResult =
      await profileMatcherService.matchResumeWithJDWithoutUploadingFile(
        appliedJob?._id,
        profile._id
      );

    // TODO : handle profile match API failure
    if (!matchResult) return;

    if (matchResult.status === "unqualified") {
      return await handleUnqualifiedProfileMatchResult(matchResult);
    } else if (matchResult.status === "qualified") {
      return await handleQualifiedProfileMatchResult(matchResult);
    }
  };

  return (
    <>
      <div>
        {jobDescription ? (
          <Content>
            {jobDescription.description && (
              <Paragraph>{jobDescription?.description}</Paragraph>
            )}
            {jobDescription.requirements &&
              jobDescription.requirements.length > 0 && (
                <div>
                  <Title level={5}>Requirements</Title>
                  <DetailedRequirementList
                    requirements={jobDescription.requirements}
                    appliedJob={appliedJob || null}
                  />
                </div>
              )}
          </Content>
        ) : (
          <Card className="requirements_container" bordered={false}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        )}
      </div>
      <ResumeUploader
        handleContinueWithProfile={handleContinueWithProfile}
        handleFileUploadAndAnalyze={handleFileUploadAndAnalyze}
        handleClose={handleToggleModal}
        isOpen={showUploadCV}
      />
    </>
  );
};

export default Requirements;
